import { useMemo, useState } from "react";
import { useLanguage } from "../../../i18n/LanguageContext";

export default function MatchingExercise({
  exercise,
  submittingLearningExercise,
  submitLearningExercise,
  styles,
}) {
  const { language } = useLanguage();

  // =========================================================
  // Canonical English pairs
  // These remain the source of truth for validation.
  // =========================================================
  const pairs = useMemo(() => {
    return exercise.exercise_data?.pairs || [];
  }, [exercise.exercise_data?.pairs]);

  // =========================================================
  // Display-only translations
  // =========================================================
  const translatedData =
    exercise.translated_exercise_data || {};

  const translatedPairs = useMemo(() => {
    if (language !== "es" && language !== "ru") {
      return [];
    }

    return translatedData.pairs || [];
  }, [language, translatedData.pairs]);

  const translatedPrompt =
    language === "es" || language === "ru"
      ? exercise.translated_prompt || null
      : null;

  const canonicalPrompt = exercise.prompt || "";

  const englishPrompt =
    canonicalPrompt.split(/[\u4e00-\u9fff]/)[0].trim();

  const chinesePromptMatch =
    canonicalPrompt.match(/[\u4e00-\u9fff].*$/);

  const chinesePrompt =
    chinesePromptMatch
      ? chinesePromptMatch[0].trim()
      : null;

  // Build translation lookup maps using canonical English values.
  const leftTranslationMap = useMemo(() => {
    const map = new Map();

    translatedPairs.forEach((pair) => {
      if (pair.canonical_left) {
        map.set(pair.canonical_left, pair.left);
      }
    });

    return map;
  }, [translatedPairs]);

  const rightTranslationMap = useMemo(() => {
    const map = new Map();

    translatedPairs.forEach((pair) => {
      if (pair.canonical_right) {
        map.set(pair.canonical_right, pair.right);
      }
    });

    return map;
  }, [translatedPairs]);

  // IMPORTANT:
  // rightOptions contains canonical English values only.
  const rightOptions = useMemo(() => {
    return [...pairs]
      .map((pair) => pair.right)
      .sort(() => Math.random() - 0.5);
  }, [pairs]);

  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);

  const handleSelect = (left, right) => {
    if (submitted) return;

    // left and right are both canonical English values.
    setAnswers((prev) => ({
      ...prev,
      [left]: right,
    }));
  };

  const handleSubmit = async () => {
    // Validation remains 100% canonical English.
    const correctCount = pairs.filter(
      (pair) => answers[pair.left] === pair.right
    ).length;

    const isCorrect =
      correctCount === pairs.length;

    const success = await submitLearningExercise({
      exerciseId: exercise.id,
      answer: answers,
      isCorrect,
    });

    if (!success) return;

    setScore({
      correct: correctCount,
      total: pairs.length,
    });

    setSubmitted(true);
  };

  const handleTryAgain = () => {
    setAnswers({});
    setSubmitted(false);
    setScore(null);
  };

  return (
    <div
      style={{
        ...styles.emptyBox,
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        Matching
      </div>

      {/* Canonical English prompt */}
      {englishPrompt && (
        <div style={styles.quizPath}>
          {englishPrompt}
        </div>
      )}

      {/* Chinese display */}
      {language === "zh" && chinesePrompt && (
        <div
          style={{
            ...styles.quizPath,
            marginTop: "4px",
            color: "#6b7280",
          }}
        >
          {chinesePrompt}
        </div>
      )}

      {/* Spanish / Russian display */}
      {translatedPrompt && (
        <div
          style={{
            ...styles.quizPath,
            marginTop: "4px",
            color: "#6b7280",
          }}
        >
          {translatedPrompt}
        </div>
      )}

      <div
        style={{
          marginTop: "16px",
          display: "grid",
          gap: "12px",
        }}
      >
        {pairs.map((pair, index) => {
          const selected =
            answers[pair.left];

          const translatedLeft =
            leftTranslationMap.get(pair.left);

          return (
            <div
              key={`${exercise.id}-${index}`}
              style={{
                padding: "12px",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
              }}
            >
              {/* Canonical English left side */}
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: translatedLeft
                    ? "3px"
                    : "8px",
                }}
              >
                {pair.left}
              </div>

              {/* Display-only translated left side */}
              {translatedLeft && (
                <div
                  style={{
                    color: "#6b7280",
                    marginBottom: "8px",
                    lineHeight: "1.5",
                  }}
                >
                  {translatedLeft}
                </div>
              )}

              <select
                style={styles.loginInput}
                value={selected || ""}
                disabled={submitted}
                onChange={(e) =>
                  handleSelect(
                    pair.left,
                    e.target.value
                  )
                }
              >
                <option value="">
                  Select match
                </option>

                {rightOptions.map((option) => {
                  const translatedRight =
                    rightTranslationMap.get(option);

                  return (
                    <option
                      key={option}
                      value={option}
                    >
                      {translatedRight
                        ? `${option} — ${translatedRight}`
                        : option}
                    </option>
                  );
                })}
              </select>

              {submitted && (
                <div style={{ marginTop: "8px" }}>
                  {selected === pair.right ? (
                    <span
                      style={{ color: "#16a34a" }}
                    >
                      Correct
                    </span>
                  ) : (
                    <span
                      style={{ color: "#dc2626" }}
                    >
                      Incorrect · Correct answer:{" "}
                      {pair.right}
                      {rightTranslationMap.get(
                        pair.right
                      )
                        ? ` — ${rightTranslationMap.get(
                            pair.right
                          )}`
                        : ""}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!submitted ? (
        <button
          style={{
            ...styles.smallPrimaryButton,
            marginTop: "16px",
          }}
          disabled={
            submittingLearningExercise ||
            Object.keys(answers).length !==
              pairs.length
          }
          onClick={handleSubmit}
        >
          {submittingLearningExercise
            ? "Submitting..."
            : "Submit Matching"}
        </button>
      ) : (
        <div style={{ marginTop: "16px" }}>
          <div style={{ fontWeight: "bold" }}>
            Score: {score.correct} / {score.total}
          </div>

          <button
            style={{
              ...styles.smallSecondaryButton,
              marginTop: "10px",
            }}
            onClick={handleTryAgain}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}